import { useTranslation } from "react-i18next";
import {
	BillingInfo,
	FrontendOrder,
	FrontendProductOrder,
	FrontendUserInfo,
	ShippingInfo,
} from "node-library/dist/portal";
import {
	AutoCompleteSearchBar,
	CommonSwitch,
	CustomCheckbox,
	CustomTextInput,
} from "../../../common-components/inputs";
import { Card, Divider, InputAdornment } from "@mui/material";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import {
	BlueButton,
	CustomIconButton,
} from "../../../common-components/button";
import {
	AlertIcon,
	CardIcon,
	TruckIcon,
} from "../../../common-components/svgIcons";
import {
	NeutralColor,
	SecondaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import { SetStateAction, useEffect, useState } from "react";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";
import { Severity } from "node-library/dist/constants";

interface Props {
	usersList: Array<FrontendUserInfo>;
	selectedUser: FrontendUserInfo | null;
	order?: FrontendProductOrder;
	setSelectedUser: React.Dispatch<SetStateAction<FrontendUserInfo | null>>;
	quoteNumber: string;
	setQuoteNumber: React.Dispatch<SetStateAction<string>>;
	quoteNumberErrorMsg: string;
	setQuoteNumberErrorMsg: React.Dispatch<SetStateAction<string>>;

	billingInfo: BillingInfo;
	setBillingInfo: React.Dispatch<SetStateAction<BillingInfo>>;

	shippingInfo: ShippingInfo;
	setShippingInfo: React.Dispatch<SetStateAction<ShippingInfo>>;

	saveInfoAsDefault: boolean;
	setSaveInfoAsDefault: React.Dispatch<SetStateAction<boolean>>;
	userListLoading: boolean;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;

	goToAddItemPage: () => void;
	goBackToOrderDetailsPage?: () => void;
	isInAdminView?: boolean;
}

export default function OrderClientDetail(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		usersList,
		selectedUser,
		setSelectedUser,
		quoteNumber,
		setQuoteNumber,
		quoteNumberErrorMsg,
		setQuoteNumberErrorMsg,

		billingInfo,
		setBillingInfo,
		shippingInfo,
		setShippingInfo,
		saveInfoAsDefault,
		setSaveInfoAsDefault,
		userListLoading,
		setUnsavedChanges,
		isInAdminView,
		goToAddItemPage,
	} = props;

	const [selectedUserErrorMsg, setselectedUserErrorMsg] = useState<string>("");

	const [userSearchText, setUserSearchText] = useState<string>("");

	const [billingErrors, setBillingErrors] = useState({
		name: "",
		company: "",
		address: "",
		city: "",
		postalCode: "",
		province: "",
		country: "",
		phoneNumber: "",
		email: "",
	});

	const [shippingErrors, setShippingErrors] = useState({
		name: "",
		address: "",
		city: "",
		postalCode: "",
		province: "",
		country: "",
		phoneNumber: "",
	});

	const billingFields = [
		{ id: "name", label: translation("ContactName") },
		{ id: "company", label: translation("CompanyName"), notRequired: true },
		{ id: "email", label: translation("Email") },
		{ id: "phoneNumber", label: translation("Phone") },
		{ id: "address", label: translation("StreetAddress") },
		{ id: "postalCode", label: translation("PostalCodeOrZip") },
		{ id: "city", label: translation("City") },
		{ id: "country", label: translation("Country") },
		{ id: "province", label: translation("ProvinceOrState") },
	];

	const shippingFields = [
		{ id: "name", label: translation("ContactName") },
		{ id: "phoneNumber", label: translation("Phone") },
		{ id: "address", label: translation("StreetAddress") },
		{ id: "postalCode", label: translation("PostalCodeOrZip") },
		{ id: "city", label: translation("City") },
		{ id: "country", label: translation("Country") },
		{ id: "province", label: translation("ProvinceOrState") },
	];

	const handleBillingChange = (field: string, value: string) => {
		setBillingInfo((prev) => ({ ...prev, [field]: value }));
	};

	const handleBillingError = (field: string, error: string) => {
		setBillingErrors((prev) => ({ ...prev, [field]: error }));
	};

	const handleShippingChange = (field: string, value: string) => {
		setShippingInfo((prev) => ({ ...prev, [field]: value }));
	};

	const handleShippingError = (field: string, error: string) => {
		setShippingErrors((prev) => ({ ...prev, [field]: error }));
	};

	const handleSubmitClientInfo = () => {
		// Simple validation for required fields
		let hasError: boolean = false;
		setBillingErrors({
			name: "",
			company: "",
			address: "",
			city: "",
			postalCode: "",
			province: "",
			country: "",
			phoneNumber: "",
			email: "",
		});
		setShippingErrors({
			name: "",
			address: "",
			city: "",
			postalCode: "",
			province: "",
			country: "",
			phoneNumber: "",
		});
		if (quoteNumber.length < 2) {
			setQuoteNumberErrorMsg(translation("MissingQuoteNumber"));
			hasError = true;
		}
		if (!selectedUser) {
			setselectedUserErrorMsg(translation("MissingClient"));
			hasError = true;
		}
		// billingFields.forEach(({ id, notRequired }) => {
		// 	const field = billingInfo[id as keyof BillingInfo];
		// 	if (!notRequired && (!field || field.length === 0)) {
		// 		hasError = true;
		// 		handleBillingError(id, translation("MissingInformation"));
		// 	}
		// });

		// shippingFields.forEach(({ id }) => {
		// 	const field = billingInfo[id as keyof BillingInfo];
		// 	if (!field || field.length === 0) {
		// 		hasError = true;
		// 		handleShippingError(id, translation("MissingInformation"));
		// 	}
		// });

		if (hasError) {
			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("MissingInformationError"),
				<YellowWarnIcon />
			);
			return;
		}

		goToAddItemPage();
	};

	useEffect(() => {
		if (selectedUser && !isInAdminView) {
			setBillingInfo({
				name:
					selectedUser.defaultBillingName ??
					`${selectedUser.firstName} ${selectedUser.lastName}`,
				address: selectedUser.defaultBillingAddress ?? "",
				company: selectedUser.defaultBillingCompany ?? "",
				city: selectedUser.defaultBillingCity ?? "",
				postalCode: selectedUser.defaultBillingPostalCode ?? "",
				province: selectedUser.defaultBillingProvince ?? "",
				country: selectedUser.defaultBillingCountry ?? "",
				phoneNumber:
					selectedUser.defaultBillingPhoneNumber ?? selectedUser.phoneNumber,
				email: selectedUser.defaultBillingEmail ?? selectedUser.userName,
			});

			setShippingInfo({
				name:
					selectedUser.defaultShippingName ??
					`${selectedUser.firstName} ${selectedUser.lastName}`,
				address: selectedUser.defaultShippingAddress ?? "",
				city: selectedUser.defaultShippingCity ?? "",
				postalCode: selectedUser.defaultShippingPostalCode ?? "",
				province: selectedUser.defaultShippingProvince ?? "",
				country: selectedUser.defaultShippingCountry ?? "",
				phoneNumber: selectedUser.defaultShippingPhoneNumber ?? "",
			});

			setBillingErrors({
				name: "",
				address: "",
				company: "",
				city: "",
				postalCode: "",
				province: "",
				country: "",
				phoneNumber: "",
				email: "",
			});
			setShippingErrors({
				name: "",
				address: "",
				city: "",
				postalCode: "",
				province: "",
				country: "",
				phoneNumber: "",
			});
		}
	}, [selectedUser]);

	const billingForm: React.ReactNode = (
		<div className="new-order-billing-form">
			{billingFields.map(({ id, label, notRequired }) => (
				<CustomTextInput
					key={id}
					id={`billing-${id}-input`}
					label={label}
					value={billingInfo[id as keyof BillingInfo]}
					setValue={(value) => {
						handleBillingChange(id, value);
						setUnsavedChanges(true);
					}}
					errorMsg={billingErrors[id as keyof BillingInfo]}
					setErrorMsg={(error) => handleBillingError(id, error)}
				/>
			))}
		</div>
	);

	const shippingForm: React.ReactNode = (
		<div className="new-order-shipping-form">
			{shippingFields.map(({ id, label }) => (
				<CustomTextInput
					key={id}
					id={`shipping-${id}-input`}
					label={label}
					value={shippingInfo[id as keyof ShippingInfo]}
					setValue={(value) => {
						handleShippingChange(id, value);
						setUnsavedChanges(true);
					}}
					errorMsg={shippingErrors[id as keyof ShippingInfo]}
					setErrorMsg={(error) => handleShippingError(id, error)}
				/>
			))}
		</div>
	);

	const quoteNumberCard: React.ReactNode = (
		<Card
			className="quote-number-card card-medium-padding order-client-detail-card"
			sx={{ backgroundColor: SecondaryColor.SECONDARY_900 }}
		>
			<div
				className="typography-heading-04 typography-bold"
				style={{ color: "white", flex: "4 1" }}
			>
				{translation("Quote")}
			</div>
			<div style={{ flex: "6 2" }}>
				<CustomTextInput
					id="quote-number-input"
					value={quoteNumber}
					setValue={(value) => {
						setQuoteNumber(value);
						setUnsavedChanges(true);
					}}
					required
					errorMsg={quoteNumberErrorMsg}
					setErrorMsg={setQuoteNumberErrorMsg}
					color="white"
					useAltHoverStyle={true}
					startAdornment={
						<InputAdornment position="start">
							<span style={{ color: NeutralColor.NEUTRAL_GREY_200 }}>
								{"#"}
							</span>
						</InputAdornment>
					}
					disabled={isInAdminView}
				></CustomTextInput>
			</div>
		</Card>
	);

	const userSearchBar: React.ReactNode = (
		<AutoCompleteSearchBar
			id="order-user-searchbar"
			options={usersList || []}
			selectedOption={selectedUser}
			setSelectedOption={setSelectedUser}
			setErrorMsg={setselectedUserErrorMsg}
			placeholder={translation("OrderUserSelectionPlaceHolder")}
			searchButtonText={translation("Search")}
			searchText={userSearchText}
			setSearchText={setUserSearchText}
			onSearch={() => {}}
			loading={userListLoading}
			disabled={isInAdminView}
		/>
	);
	const orderUserSelection: React.ReactNode = (
		<div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
			<div className="typography-heading-04 typography-bold">
				{translation("OrderUserSelectionHeading")}
			</div>
			<div className="typography-caption grey-text">
				{translation("OrderUserSelectionDescription")}
			</div>
			<div style={{ height: "40px", zIndex: "1" }}>{userSearchBar}</div>

			{selectedUserErrorMsg && selectedUserErrorMsg.length > 0 && (
				<div
					style={{
						display: "flex",
						gap: "5px",
						color: SemanticColor.ERROR,
						alignItems: "center",
					}}
				>
					<AlertIcon></AlertIcon>
					{selectedUserErrorMsg}
				</div>
			)}
		</div>
	);

	return (
		<>
			<div style={{ margin: "0 auto" }}>
				<div style={{ padding: "24px 24px" }}>
					<div
						style={{ display: "flex", width: " 100%", alignItems: "center" }}
					>
						{props.isInAdminView && (
							<CustomIconButton
								id="admin-view-back-to-order-details-button"
								size="small"
								onClick={() => {
									props.goBackToOrderDetailsPage!();
								}}
								icon={<KeyboardArrowLeftRoundedIcon />}
							></CustomIconButton>
						)}
						{quoteNumberCard}
					</div>

					<Card className="card-equal-padding" sx={{ marginTop: "20px" }}>
						{orderUserSelection}
						<div className="order-client-detail-card billing-detail-card">
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "8px",
									flex: "4 1",
								}}
							>
								<div className="order-billing-detail-header">
									<CardIcon></CardIcon>
									<div className="typography-heading-04 typography-bold">
										{translation("BillingDetails")}
									</div>
								</div>
								{/* <div
									style={{
										display: "flex",
										gap: "8px",
										alignItems: "center",
									}}
								>
									<CommonSwitch
										id="same-as-billing-details-switch"
										checked={sameBillingInfoAsClientInfo}
										setChecked={setSameBillingInfoAsClientInfo}
									/>
									<div className="typography-body-01">
										{translation("SameAsClientDetails")}
									</div>
								</div> */}
							</div>
							{billingForm}
						</div>
						<Divider orientation="horizontal" flexItem sx={{ my: 2 }}></Divider>
						<div className="order-client-detail-card shipping-detail-card">
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "8px",
									flex: "4 1",
								}}
							>
								<div className="order-billing-detail-header">
									<TruckIcon></TruckIcon>
									<div className="typography-heading-04 typography-bold">
										{translation("ShippingDetails")}
									</div>
								</div>
								<div
									style={{
										display: "flex",
										gap: "8px",
										alignItems: "center",
									}}
								>
									{/* <CommonSwitch
										id="same-as-billing-details-switch"
										checked={sameShippingInfoAsBillingInfo}
										setChecked={setSameShippingInfoAsBillingInfo}
									/>
									<div className="typography-body-01">
										{translation("SameAsBillingDetails")}
									</div> */}
								</div>
							</div>
							<div className="new-order-shipping-content">
								{shippingForm}
								<CustomCheckbox
									className={`save-info-as-default-checkbox`}
									label={translation("SaveInfoAsDefaultDescription")}
									labelClassName={"typography-body-01"}
									checked={saveInfoAsDefault}
									setChecked={setSaveInfoAsDefault}
								/>
								<BlueButton
									size={"flex"}
									id={"new-order-client-detail-continue-button"}
									onClick={() => {
										handleSubmitClientInfo();
									}}
								>
									{translation("Continue")}
								</BlueButton>
							</div>
						</div>
					</Card>
				</div>
			</div>
		</>
	);
}
